/**
 * Settings.
 *
 * Three tabs, in the order an owner needs them: the business block that prints
 * at the top of every receipt, the receipt layout itself, and then the
 * preferences that change how the till behaves.
 *
 * The receipt tab shows a live preview at real paper width, because the only
 * way to know a receipt is right is to look at one.
 */

import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiSave, FiPrinter, FiSettings, FiHome, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';

import { setupApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import { PageHeader } from '../components/ui/Feedback';
import { SpinnerBlock } from '../components/ui/Spinner';
import { Field, Input, NumberInput, Select, Textarea, Toggle, Checkbox } from '../components/ui/Field';
import ImagePicker from '../components/ui/ImagePicker';
import Receipt from '../components/print/Receipt';
import { loadLookups, selectLookups } from '../store/menuSlice';

/* --------------------------- a fake receipt to preview -------------------- */

/**
 * A worked example rather than a real order, so the preview works on a fresh
 * install with no sales yet - which is exactly when someone is setting the
 * receipt up.
 */
const sampleReceipt = (business, template) => ({
    template: {
        paperWidthMM: template.Paper_Width_MM,
        contentWidthMM: template.Content_Width_MM || null,
        fontSizePx: template.Font_Size_PX,
        printCopies: template.Print_Copies,
        showLogo: !!template.Show_Logo,
        showAddress: !!template.Show_Address,
        showNtnStrn: !!template.Show_NTN_STRN,
        showCashier: !!template.Show_Cashier,
        showTableNo: !!template.Show_Table_No,
        showItemDiscount: !!template.Show_Item_Discount,
        showTaxBreakup: !!template.Show_Tax_Breakup,
        showFiscalNumber: !!template.Show_Fiscal_Number,
        showQrCode: !!template.Show_QR_Code,
        showAuthorityLogo: !!template.Show_Authority_Logo,
        headerText: template.Header_Text,
        footerText: template.Footer_Text
    },
    business: {
        name: business?.Business_Name || 'Your Restaurant',
        addressLines: [business?.Address_Line1, business?.Address_Line2, business?.Address_Line3]
            .filter(Boolean),
        phone: business?.Phone_No,
        mobile: business?.Mobile_No,
        ntn: business?.NTN,
        strn: business?.STRN,
        logoPath: business?.Logo_Path
    },
    outlet: {},
    invoice: {
        orderId: 0,
        orderNumber: 'ORD-20260903-000042',
        invoiceNumber: 'INV-000042',
        fiscalInvoiceNumber: '12345678901234567',
        fiscalStatus: 'ACCEPTED',
        authorityShortName: 'PRA',
        isProduction: true,
        posId: '123456',
        date: new Date(),
        orderType: 'Dine In',
        orderTypeCode: 'DINE_IN',
        tableNumber: '5',
        guestCount: 4,
        cashierName: 'Ali Raza',
        reprintCount: 0
    },
    items: [
        { serial: 1, description: 'Chicken Tikka Pizza - Large', modifiers: [{ name: 'Extra Cheese', price: 150 }], rate: 1450, quantity: 1, discount: 0, amount: 1600 },
        { serial: 2, description: 'Zinger Burger', modifiers: [], rate: 450, quantity: 2, discount: 0, amount: 900 },
        { serial: 3, description: 'Soft Drink 345ml', modifiers: [], rate: 120, quantity: 2, discount: 0, amount: 240 }
    ],
    totals: {
        currency: 'Rs.', itemCount: 3, totalQuantity: 5,
        grossAmount: 2740, discount: 0, subTotal: 2740,
        serviceCharge: 0, serviceChargePercent: 0, deliveryCharge: 0,
        taxAmount: 377.93, posServiceFee: 1, roundOff: 0.07,
        totalPayable: 2741, paidAmount: 3000, changeAmount: 259,
        amountInWords: 'Rupees Two Thousand Seven Hundred Forty One Only'
    },
    taxBreakup: [{ label: '16%', percent: 16, taxableAmount: 2362.07, taxAmount: 377.93 }],
    payments: [{ mode: 'Cash', modeCode: 'CASH', amount: 2741, tendered: 3000, change: 259 }],
    qrCode: null,
    qrPayload: '12345678901234567',
    printedAt: new Date()
});

/* --------------------------------- the page ------------------------------- */

const SettingsPage = () => {
    const dispatch = useDispatch();
    const lookups = useSelector(selectLookups);
    const [tab, setTab] = useState('business');

    const loadBusiness = useCallback(() => setupApi.getBusiness(), []);
    const { data: businessData, loading: businessLoading, refetch: refetchBusiness } =
        useAsync(loadBusiness, []);

    const loadSettings = useCallback(() => setupApi.listSettings(), []);
    const { data: settingGroups, loading: settingsLoading, refetch: refetchSettings } =
        useAsync(loadSettings, []);

    const loadTemplates = useCallback(() => setupApi.listReceiptTemplates(), []);
    const { data: templates, refetch: refetchTemplates } = useAsync(loadTemplates, []);

    const [business, setBusiness] = useState(null);
    const [template, setTemplate] = useState(null);
    const [settingEdits, setSettingEdits] = useState({});

    useEffect(() => {
        if (businessData?.business) setBusiness({ ...businessData.business });
    }, [businessData]);

    useEffect(() => {
        if (templates?.length) setTemplate({ ...templates.find((t) => t.Is_Default) || templates[0] });
    }, [templates]);

    const { run: saveBusiness, busy: savingBusiness } = useAction(
        () => setupApi.updateBusiness(business),
        {
            successMessage: 'Business details saved.',
            onSuccess: () => { refetchBusiness(); dispatch(loadLookups()); }
        }
    );

    const { run: saveTemplate, busy: savingTemplate } = useAction(
        () => setupApi.updateReceiptTemplate(template.Receipt_Template_ID, template),
        { successMessage: 'Receipt layout saved.', onSuccess: refetchTemplates }
    );

    const { run: saveSettings, busy: savingSettings } = useAction(
        () => setupApi.updateSettings(settingEdits),
        {
            successMessage: 'Settings saved.',
            onSuccess: () => { setSettingEdits({}); refetchSettings(); dispatch(loadLookups()); }
        }
    );

    if (businessLoading || settingsLoading) return <SpinnerBlock label="Loading settings…" />;

    const settingValue = (row) => {
        if (Object.prototype.hasOwnProperty.call(settingEdits, row.Setting_Name)) {
            return settingEdits[row.Setting_Name];
        }
        return row.Setting_Value;
    };

    const setSetting = (name, value) => setSettingEdits((current) => ({ ...current, [name]: value }));

    return (
        <div className="page">
            <PageHeader title="Settings" subtitle="The business, the receipt and how the till behaves" />

            <div className="flex gap-2 overflow-x-auto scrollbar-none border-b border-slate-200">
                {[
                    { key: 'business', label: 'Business', icon: FiHome },
                    { key: 'receipt', label: 'Receipt', icon: FiPrinter },
                    { key: 'preferences', label: 'Preferences', icon: FiSettings }
                ].map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => setTab(item.key)}
                        className={`-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5
                                    text-sm font-medium transition-colors sm:px-4 lg:py-2
                                    ${tab === item.key
                                        ? 'border-brand-600 text-brand-800'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        <item.icon className="h-4 w-4" /> {item.label}
                    </button>
                ))}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {/* ------------------------------------------------ business */}
                {tab === 'business' && business && (
                    <div className="max-w-3xl space-y-4">
                        <p className="text-sm text-slate-500">
                            This is the block printed at the top of every receipt, and the seller
                            details sent to FBR or PRA on every invoice.
                        </p>

                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Business name" required>
                                <Input
                                    value={business.Business_Name || ''}
                                    onChange={(e) => setBusiness({ ...business, Business_Name: e.target.value })}
                                />
                            </Field>
                            <Field label="Legal name" hint="If it differs from the trading name.">
                                <Input
                                    value={business.Business_Legal_Name || ''}
                                    onChange={(e) => setBusiness({ ...business, Business_Legal_Name: e.target.value })}
                                />
                            </Field>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-3">
                            <Field label="NTN" hint="Sent as the seller identifier.">
                                <Input
                                    value={business.NTN || ''}
                                    onChange={(e) => setBusiness({ ...business, NTN: e.target.value })}
                                />
                            </Field>
                            <Field label="STRN">
                                <Input
                                    value={business.STRN || ''}
                                    onChange={(e) => setBusiness({ ...business, STRN: e.target.value })}
                                />
                            </Field>
                            <Field label="CNIC" hint="For a sole proprietor with no NTN.">
                                <Input
                                    value={business.CNIC || ''}
                                    onChange={(e) => setBusiness({ ...business, CNIC: e.target.value })}
                                />
                            </Field>
                        </div>

                        <div className="space-y-2">
                            <Field label="Address line 1">
                                <Input
                                    value={business.Address_Line1 || ''}
                                    onChange={(e) => setBusiness({ ...business, Address_Line1: e.target.value })}
                                />
                            </Field>
                            <Input
                                placeholder="Address line 2"
                                value={business.Address_Line2 || ''}
                                onChange={(e) => setBusiness({ ...business, Address_Line2: e.target.value })}
                            />
                            <Input
                                placeholder="Address line 3"
                                value={business.Address_Line3 || ''}
                                onChange={(e) => setBusiness({ ...business, Address_Line3: e.target.value })}
                            />
                        </div>

                        <div className="grid gap-3 sm:grid-cols-3">
                            <Field label="Province" hint="Sent on every fiscal invoice.">
                                <Select
                                    value={business.Province_ID || ''}
                                    onChange={(e) => setBusiness({ ...business, Province_ID: Number(e.target.value) })}
                                >
                                    <option value="">Choose…</option>
                                    {lookups.provinces.map((p) => (
                                        <option key={p.Province_ID} value={p.Province_ID}>{p.Province_Name}</option>
                                    ))}
                                </Select>
                            </Field>
                            <Field label="Phone">
                                <Input
                                    value={business.Phone_No || ''}
                                    onChange={(e) => setBusiness({ ...business, Phone_No: e.target.value })}
                                />
                            </Field>
                            <Field label="Mobile">
                                <Input
                                    value={business.Mobile_No || ''}
                                    onChange={(e) => setBusiness({ ...business, Mobile_No: e.target.value })}
                                />
                            </Field>
                        </div>

                        <Field label="Logo">
                            <ImagePicker
                                noun="logo"
                                value={business.Logo_Path || null}
                                onChange={(path) => setBusiness({ ...business, Logo_Path: path })}
                                upload={setupApi.uploadLogo}
                                remove={setupApi.deleteImage}
                                fit="contain"
                                boxClassName="h-24 w-40"
                                hint="Printed at the top of the receipt, when the receipt layout has Logo ticked. A thermal printer prints in black and white, so a plain logo reads better than a photograph."
                            />
                        </Field>

                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => saveBusiness()}
                            disabled={savingBusiness}
                        >
                            <FiSave /> {savingBusiness ? 'Saving…' : 'Save business details'}
                        </button>
                    </div>
                )}

                {/* ------------------------------------------------- receipt */}
                {tab === 'receipt' && template && (
                    <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:gap-6">
                        <div className="max-w-xl space-y-4">
                            <div className="grid gap-3 sm:grid-cols-2">
                                <Field label="Paper width">
                                    <Select
                                        value={template.Paper_Width_MM}
                                        onChange={(e) => setTemplate({
                                            ...template, Paper_Width_MM: Number(e.target.value)
                                        })}
                                    >
                                        <option value={80}>80 mm (standard)</option>
                                        <option value={58}>58 mm (compact)</option>
                                    </Select>
                                </Field>
                                <Field label="Copies to print">
                                    <Input
                                        type="number"
                                        min={1}
                                        max={5}
                                        value={template.Print_Copies}
                                        onChange={(e) => setTemplate({
                                            ...template, Print_Copies: Number(e.target.value)
                                        })}
                                    />
                                </Field>

                                <Field
                                    label="Font size"
                                    hint="Smaller fits more on a narrow printer without wrapping."
                                >
                                    <div className="flex items-center gap-2">
                                        <NumberInput
                                            className="max-w-[8rem]"
                                            min={8}
                                            max={16}
                                            step={1}
                                            value={template.Font_Size_PX}
                                            onChange={(e) => setTemplate({
                                                ...template, Font_Size_PX: Number(e.target.value)
                                            })}
                                        />
                                        <span className="text-sm text-slate-500">px</span>
                                    </div>
                                </Field>

                                <Field
                                    label="Printable width override"
                                    className="sm:col-span-2"
                                    hint={
                                        `Leave blank to use the standard width (72mm for 80mm paper, `
                                        + `48mm for 58mm). If receipts still lose their right edge after `
                                        + `setting Margins to None in the print dialog, that means this `
                                        + `printer's own usable width is narrower than standard - lower `
                                        + `this a few mm at a time and reprint until nothing is cut.`
                                    }
                                >
                                    <div className="flex items-center gap-2">
                                        <NumberInput
                                            className="max-w-[8rem]"
                                            min={30}
                                            max={template.Paper_Width_MM}
                                            step={1}
                                            placeholder="auto"
                                            value={template.Content_Width_MM ?? ''}
                                            onChange={(e) => setTemplate({
                                                ...template,
                                                Content_Width_MM: e.target.value === ''
                                                    ? null : Number(e.target.value)
                                            })}
                                        />
                                        <span className="text-sm text-slate-500">mm</span>
                                        {template.Content_Width_MM != null && (
                                            <button
                                                type="button"
                                                className="btn-ghost btn-sm"
                                                onClick={() => setTemplate({ ...template, Content_Width_MM: null })}
                                            >
                                                Use standard
                                            </button>
                                        )}
                                    </div>
                                </Field>
                            </div>

                            <div className="space-y-2 rounded-xl bg-slate-50 p-4">
                                <p className="mb-1 text-sm font-medium text-slate-700">What to print</p>
                                {[
                                    ['Show_Logo', 'Logo'],
                                    ['Show_Address', 'Address'],
                                    ['Show_NTN_STRN', 'NTN and STRN'],
                                    ['Show_Cashier', 'Cashier name'],
                                    ['Show_Table_No', 'Table number'],
                                    ['Show_Item_Discount', 'Discount on each line'],
                                    ['Show_Tax_Breakup', 'Sales tax broken down by rate'],
                                    ['Show_Fiscal_Number', 'FBR / PRA invoice number'],
                                    ['Show_QR_Code', 'QR code']
                                ].map(([key, label]) => (
                                    <Checkbox
                                        key={key}
                                        label={label}
                                        checked={!!template[key]}
                                        onChange={(v) => setTemplate({ ...template, [key]: v })}
                                    />
                                ))}
                            </div>

                            <Field label="Header text" hint="Printed under the address.">
                                <Textarea
                                    rows={2}
                                    value={template.Header_Text || ''}
                                    onChange={(e) => setTemplate({ ...template, Header_Text: e.target.value })}
                                />
                            </Field>

                            <Field label="Footer text">
                                <Textarea
                                    rows={2}
                                    value={template.Footer_Text || ''}
                                    onChange={(e) => setTemplate({ ...template, Footer_Text: e.target.value })}
                                    placeholder="Thank you for dining with us"
                                />
                            </Field>

                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => saveTemplate()}
                                disabled={savingTemplate}
                            >
                                <FiSave /> {savingTemplate ? 'Saving…' : 'Save receipt layout'}
                            </button>
                        </div>

                        {/* Live preview at real paper width */}
                        <div className="shrink-0">
                            <p className="mb-2 text-sm font-medium text-slate-600">
                                Preview ({template.Paper_Width_MM} mm)
                            </p>
                            <div className="rounded-xl bg-slate-200 p-4">
                                <Receipt
                                    receipt={sampleReceipt(business || businessData?.business, template)}
                                    isPreview
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------------------------------------------- preferences */}
                {tab === 'preferences' && (
                    <div className="max-w-3xl space-y-5">
                        {Object.entries(settingGroups || {}).map(([groupName, rows]) => (
                            <section key={groupName} className="card p-4">
                                <h2 className="mb-3 font-semibold text-slate-800">
                                    {groupName.charAt(0) + groupName.slice(1).toLowerCase()}
                                </h2>

                                <div className="space-y-4">
                                    {rows.map((row) => (
                                        row.Setting_Data_Type === 'BOOLEAN' ? (
                                            <Toggle
                                                key={row.Setting_Name}
                                                label={row.Setting_Name.replace(/_/g, ' ').toLowerCase()}
                                                description={row.Description}
                                                checked={String(settingValue(row)).toLowerCase() === 'true'}
                                                onChange={(v) => setSetting(row.Setting_Name, v ? 'true' : 'false')}
                                            />
                                        ) : (
                                            <Field
                                                key={row.Setting_Name}
                                                label={row.Setting_Name.replace(/_/g, ' ').toLowerCase()}
                                                hint={row.Description}
                                            >
                                                <Input
                                                    type={row.Setting_Data_Type === 'NUMBER' ? 'number' : 'text'}
                                                    value={settingValue(row)}
                                                    onChange={(e) => setSetting(row.Setting_Name, e.target.value)}
                                                    className={row.Setting_Data_Type === 'NUMBER' ? 'tabular max-w-[12rem]' : ''}
                                                />
                                            </Field>
                                        )
                                    ))}
                                </div>
                            </section>
                        ))}

                        <div className="sticky bottom-0 flex items-center gap-2 border-t border-slate-200 bg-slate-100 py-3">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => saveSettings()}
                                disabled={savingSettings || Object.keys(settingEdits).length === 0}
                            >
                                <FiSave />
                                {savingSettings
                                    ? 'Saving…'
                                    : `Save ${Object.keys(settingEdits).length || ''} change(s)`}
                            </button>

                            {Object.keys(settingEdits).length > 0 && (
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setSettingEdits({})}
                                >
                                    <FiRefreshCw /> Discard
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;
